// src/graphql/tours.js
// Queries for TourDetails.jsx and TourExpedition.jsx
//
// SCHEMA ALIGNMENT (v4.0 model refactor)
//
// TourType — logistics fields now resolved from TourLogistics (1-1):
//   arrivalAirport, arrivalAirportName, arrivalTransferNotes,
//   meetingPoint, meetingPointNotes, recommendedBestSeason,
//   minAge, guideTierLabel, preDepartureSteps, climateMonths,
//   packingCategories, healthRequirements
//   → These are declared as explicit graphene fields on TourType
//     resolved via tour.logistics. No change needed on the client —
//     the field names are identical. ✓
//
// TourType — stats fields now resolved from TourStats (1-1):
//   averageRating, totalReviews, totalBookings, popularityScore
//   → Same pattern — field names unchanged on the schema surface. ✓
//
// AgencyType — three fields were requested that don't exist on Agency:
//   emergencyPhone        → REMOVED (not on Agency model; use phone)
//   preDepartureEmail     → REMOVED (not on Agency model)
//   climateRecommendation → REMOVED (not on Agency model)
//   The AGENCY_FRAGMENT now requests only fields the model actually has.
//
// CountryType — three fields were requested that don't exist:
//   flagImage             → REMOVED (not on Country model)
//   frontendContinentSlug → REMOVED (not on Country model)
//   tourCount             → REMOVED (this is an annotation; not on
//                           the model or CountryType DjangoObjectType)
//   GET_TOUR_COUNTRIES and GET_TOUR_DESTINATIONS now request only
//   fields that exist on Country (id, name, slug, code).
//
// Park.shortDescription   → REMOVED from GET_TOUR_DESTINATIONS
//   (Park model has `description`, not `shortDescription`)
//
// additionalCosts → correct camelCase for the resolver field name. ✓
//
// All other field names are unchanged — clients see the same data.

import { gql } from '@apollo/client';


// ─────────────────────────────────────────────────────────────────────────────
// FRAGMENTS
// ─────────────────────────────────────────────────────────────────────────────

const GUIDE_ASSIGNMENT_FRAGMENT = gql`
  fragment GuideAssignmentFields on TourGuideAssignmentType {
    id
    role
    roleDisplay
    isPrimary
    sortOrder
    guide {
      id
      bio
      shortBio
      photo
      experienceYears
      rating
      totalReviews
      languages
      certifications
      specializations
      user {
        id
        firstName
        lastName
        fullName
      }
    }
  }
`;

const HIGHLIGHT_FRAGMENT = gql`
  fragment HighlightFields on TourHighlightAssignmentType {
    id
    sortOrder
    highlight {
      id
      iconName
      label
      description
    }
  }
`;

const CANCELLATION_POLICY_FRAGMENT = gql`
  fragment CancellationPolicyFields on CancellationPolicyType {
    id
    name
    policyType
    description
    freeDateChanges
    freeDateChangesNote
    allowTransfer
    transferNote
    isDefault
    isActive
    tiers {
      id
      windowLabel
      refundPercentage
      refundDisplay
      tierType
      description
      hoursBeforeDeparture
      sortOrder
    }
  }
`;

const DEPARTURE_FRAGMENT = gql`
  fragment DepartureFields on TourDepartureType {
    id
    departureDate
    returnDate
    availableSpots
    bookedSpots
    spotsRemaining
    isFull
    capacityPct
    status
  }
`;

// Only fields that exist on AgencyType.
// emergencyPhone, preDepartureEmail, and climateRecommendation were removed —
// they don't exist on the Agency model.
const AGENCY_FRAGMENT = gql`
  fragment AgencyFields on AgencyType {
    id
    name
    slug
    logo
    phone
    primaryEmail
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// TOUR DETAILS PAGE
// Feeds TourDetails.jsx — overview, summary, itinerary, logistics tabs
// ─────────────────────────────────────────────────────────────────────────────

export const GET_TOUR_DETAILS = gql`
  ${GUIDE_ASSIGNMENT_FRAGMENT}
  ${HIGHLIGHT_FRAGMENT}
  ${CANCELLATION_POLICY_FRAGMENT}
  ${DEPARTURE_FRAGMENT}
  ${AGENCY_FRAGMENT}

  query GetTourDetails($slug: String!) {
    tour(slug: $slug) {
      id
      slug
      name
      tagline
      shortDescription
      description
      durationDays
      durationNights
      durationLabel
      difficultyLevel
      difficultyLabel
      minParticipants
      maxParticipants
      totalReviews
      averageRating
      featuredImage
      galleryImages
      includes
      excludes
      mainActivities
      differentiators
      pullQuote
      pullQuoteBy
      agencyServiceFee
      currentPrice
      isMultiCountry
      guideLanguages

      # ── Quick Facts (Summary tab) ─────────────────────────────────────
      # Resolved from TourLogistics via tour.logistics (schema unchanged)
      recommendedBestSeason
      minAge
      guideTierLabel

      # ── Logistics tab — arrival ───────────────────────────────────────
      arrivalAirport
      arrivalAirportName
      arrivalTransferNotes
      meetingPoint
      meetingPointNotes

      # ── Logistics tab — structured content ───────────────────────────
      # Each item is a JSONString dict; parse on the client:
      #   const parsed = typeof item === 'string' ? JSON.parse(item) : item
      preDepartureSteps
      climateMonths
      packingCategories
      healthRequirements

      category {
        id
        name
        slug
        icon
      }

      parks {
        id
        name
        slug
        country {
          name
          code
        }
      }

      highlightAssignments {
        ...HighlightFields
      }

      guideAssignments {
        ...GuideAssignmentFields
      }

      cancellationPolicy {
        ...CancellationPolicyFields
      }

      upcomingDepartures(limit: 6) {
        ...DepartureFields
      }

      itineraryDays {
        id
        dayNumber
        timeLabel
        durationLabel
        title
        locationLabel
        category
        categoryDisplay
        description
        details
        insiderTip
        includesAtStop
        isOvernight
        primaryPark {
          id
          name
        }
        accommodation {
          id
          name
          featuredImage
        }
      }

      agency {
        ...AgencyFields
      }
    }
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// TOUR EXPEDITION / PACKAGE PAGE
// Feeds TourExpedition.jsx — full package view with map pins
// ─────────────────────────────────────────────────────────────────────────────

export const GET_TOUR_EXPEDITION = gql`
  ${GUIDE_ASSIGNMENT_FRAGMENT}
  ${HIGHLIGHT_FRAGMENT}
  ${CANCELLATION_POLICY_FRAGMENT}
  ${DEPARTURE_FRAGMENT}
  ${AGENCY_FRAGMENT}

  query GetTourExpedition($slug: String!) {
    tour(slug: $slug) {
      id
      slug
      name
      tagline
      shortDescription
      description
      durationDays
      durationNights
      durationLabel
      difficultyLevel
      difficultyLabel
      minParticipants
      maxParticipants

      totalReviews
      averageRating
      featuredImage
      galleryImages
      includes
      excludes
      mainActivities
      differentiators
      pullQuote
      pullQuoteBy
      agencyServiceFee
      currentPrice
      guideLanguages
      recommendedBestSeason
      minAge
      guideTierLabel
      arrivalAirport
      arrivalAirportName
      arrivalTransferNotes
      meetingPoint
      meetingPointNotes
      preDepartureSteps
      climateMonths
      packingCategories
      healthRequirements

      category {
        id
        name
        slug
        icon
      }

      highlightAssignments {
        ...HighlightFields
      }

      guideAssignments {
        ...GuideAssignmentFields
      }

      cancellationPolicy {
        ...CancellationPolicyFields
      }

      upcomingDepartures(limit: 8) {
        ...DepartureFields
      }

      # Full itinerary days with map pins for the interactive route map
      itineraryDays {
        id
        dayNumber
        timeLabel
        durationLabel
        title
        locationLabel
        category
        categoryDisplay
        description
        details
        insiderTip
        includesAtStop
        driveFromPreviousKm
        isOvernight
        mapPin
        location {
          id
          name
          shortLabel
          latitude
          longitude
          elevationM
          locationType
          country {
            name
            code
          }
        }
        primaryPark {
          id
          name
          slug
        }
        accommodation {
          id
          name
          slug
          featuredImage
        }
      }

      parks {
        id
        name
        slug
        country {
          name
          code
        }
      }

      additionalCosts {
        id
        name
        description
        note
        icon
        costType
        priceDisplay
        pricePer
        sortOrder
      }

      reviews(limit: 6) {
        id
        authorName
        title
        body
        rating
        reviewedAt
        isVerifiedGuest
        sourceType
        helpfulCount
        media {
          id
          url
          thumbnailUrl
          caption
        }
      }

      faqs {
        id
        question
        answer
        sortOrder
      }

      agency {
        ...AgencyFields
      }
    }
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// TOUR CATEGORIES  (for listings / nav)
// ─────────────────────────────────────────────────────────────────────────────
export const GET_TOUR_CATEGORIES = gql`
  query GetTourCategories {
    tourCategories {
      id
      name
      slug
      heroImage
      description
    }
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// CANCELLATION POLICIES  (for static policy page)
// ─────────────────────────────────────────────────────────────────────────────

export const GET_CANCELLATION_POLICIES = gql`
  ${CANCELLATION_POLICY_FRAGMENT}
  query GetCancellationPolicies {
    cancellationPolicies {
      ...CancellationPolicyFields
    }
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// ALL TOURS  (listings page with pagination)
// ─────────────────────────────────────────────────────────────────────────────

export const GET_ALL_TOURS = gql`
  query GetAllTours(
    $limit: Int
    $offset: Int
    $categorySlug: String
    $search: String
    $minDurationDays: Int
    $maxDurationDays: Int
    $minPrice: Float
    $maxPrice: Float
  ) {
    tours(
      limit: $limit
      offset: $offset
      categorySlug: $categorySlug
      search: $search
      minDurationDays: $minDurationDays
      maxDurationDays: $maxDurationDays
      minPrice: $minPrice
      maxPrice: $maxPrice
    ) {
      id
      slug
      name
      tagline
      featuredImage
      currentPrice
      durationLabel
      maxParticipants
      totalReviews
      averageRating
      category {
        id
        name
        slug
      }
      parks {
        id
        name
        country {
          id
          name
        }
      }
    }
    toursCount(
      categorySlug: $categorySlug
      search: $search
      minDurationDays: $minDurationDays
      maxDurationDays: $maxDurationDays
      minPrice: $minPrice
      maxPrice: $maxPrice
    )
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// HOME FEATURED TOURS
// ─────────────────────────────────────────────────────────────────────────────

export const GET_HOME_FEATURED_TOURS = gql`
  query GetHomeFeaturedTours {
    homeFeaturedTours {
      id
      slug
      name
      tagline
      featuredImage
      currentPrice
      durationLabel
      maxParticipants

      totalReviews

      averageRating
      category {
        name
      }
      parks {
        name
        country {
          name
        }
      }
    }
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// TOUR COUNTRIES
// Only fields that exist on CountryType / the Country model.
// flagImage, frontendContinentSlug, and tourCount were removed —
// they are not on the Country model or the CountryType DjangoObjectType.
// Add a resolver to CountryType if these fields are needed in future.
// ─────────────────────────────────────────────────────────────────────────────
export const GET_TOUR_COUNTRIES = gql`
  query GetTourCountries {
    tourCountries {
      id
      name
      slug
      code
      heroImage
      flagImage
      frontendContinentSlug
      tourCount
    }
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// TOUR DESTINATIONS  (country + parks for the destinations page)
// Park.shortDescription removed — the Park model has `description`, not
// `shortDescription`. If a truncated version is needed, add a resolver
// to ParkType that returns description[:200].
// ─────────────────────────────────────────────────────────────────────────────

export const GET_TOUR_DESTINATIONS = gql`
  query GetTourDestinations {
    tourCountries {
      id
      name
      slug
      frontendContinentSlug
      continentName          
      parks {
        id
        name
        slug
        shortDescription
        featuredImage
      }
    }
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// TOUR ITINERARY  (itinerary tab standalone query)
// additionalCosts is the correct camelCase for the resolver field.
// ─────────────────────────────────────────────────────────────────────────────

export const GET_TOUR_ITINERARY = gql`
  query GetTourItinerary($slug: String!) {
    tour(slug: $slug) {
      id
      name
      slug
      tagline
      featuredImage
      durationLabel
      duration: durationDays
      difficulty: difficultyLevel
      maxParticipants
      includes
      excludes
      mainActivities
      parks {
        name
        country {
          name
        }
      }
      additionalCosts {
        id
        name
        description
        note
        icon
        costType
        priceDisplay
        pricePer
        sortOrder
      }
      itineraryDays {
        id
        dayNumber
        title
        category
        location {
          name
        }
        primaryPark {
          name
          latitude
          longitude
        }
        accommodation {
          name
        }
        description
        insiderTip
        timeLabel
        durationLabel
        locationLabel
        details
        includesAtStop
        driveFromPreviousKm
        isOvernight
      }
    }
  }
`;